// @ts-nocheck
import React, { useState } from "react";
import { TrailerModelV2, TrailerPickupRequestModel } from "@store/Framework/Types/ApiModels";
import TrailerTrackingPickupRequestModal from "@store/Library/Components/TrailerTrackingV2/TrailerTrackingPickupRequestModal";
import Link from "@insite/mobius/Link";
import { Typography } from "@insite/mobius/index";
import translate from "@insite/client-framework/Translate";
import siteMessage from "@insite/client-framework/SiteMessage";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { LanguageModel } from "@insite/client-framework/Types/ApiModels";
import Modal from "@store/MobiusOverrides/Modal/Modal";
import Clickable from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import AlertCircle from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import { css } from "styled-components";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { useTrailerTrackingV2State } from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/TrailerTrackingV2Hooks";
import { getTrailerPickupRequestKey } from "@store/Framework/Services/TrailerServiceV2";

export interface TrailerTrackingPickupRequestButtonStyles {
    tooltipIcon?: IconPresentationProps;
    requestedTextWrapper?: InjectableCss;
}

export const trailerTrackingPickupRequestButtonStyles: TrailerTrackingPickupRequestButtonStyles = {
    tooltipIcon: {
        size: 16,
        color: "inherit",
    },
    requestedTextWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(8))}
            display: flex;
            align-items: center;
            gap: ${pxToRem(4)};
        `,
    },
};

const styles = trailerTrackingPickupRequestButtonStyles;

export interface TrailerTrackingPickupRequestButtonProps {
    shipToNumber: string;
    shipToAddress: string;
    trailer: TrailerModelV2;
}

const getPickupRequestedTooltipText = (
    trailerPickupRequest: TrailerPickupRequestModel,
    language: LanguageModel | null,
) => {
    if (!trailerPickupRequest) {
        return "";
    }

    const requestedOn = new Date(trailerPickupRequest.requestedOn)?.toLocaleDateString(language?.languageCode) || "";
    const isBobtail = trailerPickupRequest.pickupOption === "bobtail";

    if (isBobtail) {
        if (trailerPickupRequest.requestedPickupDate) {
            const requestedPickupOn =
                new Date(trailerPickupRequest?.requestedPickupDate)?.toLocaleDateString(language?.languageCode) || "";

            return siteMessage(
                "TrailerTracking_PickupRequest_PickupRequested_Bobtail",
                requestedOn,
                requestedPickupOn,
            ) as string;
        }

        return siteMessage(
            "TrailerTracking_PickupRequest_PickupRequested_Bobtail_NoRequestedDate",
            requestedOn,
        ) as string;
    }

    return siteMessage("TrailerTracking_PickupRequest_PickupRequested_NextDelivery", requestedOn) as string;
};

const TrailerTrackingPickupRequestButton = ({
    trailer,
    shipToNumber,
    shipToAddress,
}: TrailerTrackingPickupRequestButtonProps) => {
    const [isPickupRequestModalOpen, setIsPickupRequestModalOpen] = useState<boolean>(false);
    const [isTooltipModalOpen, setIsTooltipModalOpen] = useState(false);
    const language = useAppSelector(state => state.context.session.language);
    const trailerPickupRequests = useTrailerTrackingV2State().trailerPickupRequests;
    const toaster = useToaster();

    const handleRequestPickupButtonClick = () => {
        setIsPickupRequestModalOpen(true);
    };

    const handleCancelPickupRequest = () => {
        setIsPickupRequestModalOpen(false);
    };

    const handleSubmitPickupRequest = () => {
        setIsPickupRequestModalOpen(false);
        toaster.addToast({
            body: siteMessage("TrailerTracking_PickupRequest_PickupRequested_Notification"),
            messageType: "success",
        });
    };

    const handleTooltipClose = () => {
        setIsTooltipModalOpen(false);
    };

    const handleTooltipClick = () => {
        setIsTooltipModalOpen(true);
    };

    // For some reason, some trailers are coming back with no shipmentNumber. Those are not eligible for pickup requests.
    if (!trailer.shipmentNumber?.length || !trailer.id?.length) {
        return null;
    }

    const pickupRequestKey = getTrailerPickupRequestKey({
        trailerId: trailer.id,
        shipmentNumber: trailer.shipmentNumber,
        shipToNumber,
    });
    const pickupRequest = trailer.pickupRequest ?? trailerPickupRequests[pickupRequestKey];
    const isPickupRequested = Boolean(pickupRequest);

    return (
        <>
            {isPickupRequested ? (
                <StyledWrapper {...styles.requestedTextWrapper}>
                    <Typography>{translate("Pickup requested")}</Typography>
                    <Clickable onClick={handleTooltipClick}>
                        <Icon {...styles.tooltipIcon} src={AlertCircle} />
                    </Clickable>
                </StyledWrapper>
            ) : (
                <Link onClick={handleRequestPickupButtonClick}>{translate("Request Pickup")}</Link>
            )}

            <TrailerTrackingPickupRequestModal
                isOpen={isPickupRequestModalOpen}
                pickupRequestItem={{
                    trailer,
                    shipToNumber,
                    shipToAddress,
                }}
                onCancel={handleCancelPickupRequest}
                onSubmitPickupRequest={handleSubmitPickupRequest}
            />
            <Modal
                isOpen={isTooltipModalOpen}
                handleClose={handleTooltipClose}
                headline={translate("Pickup requested")}
                sizeVariant={"small"}
            >
                {getPickupRequestedTooltipText(pickupRequest, language)}
            </Modal>
        </>
    );
};

export default TrailerTrackingPickupRequestButton;
